<template>
  <b-card class="mb-0 product-card">
    <b-dropdown
      v-if="!isDiscarded"
      id="dropdown-1"
      toggle-class="my-custom-toggle"
      size="sm"
      right
      no-caret
      variant="outline-none"
    >
      <template #button-content>
        <feather-icon
          icon="MoreVerticalIcon"
          size="18"
          color="#000"
        />
      </template>
      <!-- <b-dropdown-item
        v-if="currentUser.role_name === 'admin'"
        :to="{ name: 'editProduct', params: { productId: product.id } }"
      >
        {{ $t('edit') }}
      </b-dropdown-item> -->
      <b-dropdown-item v-b-modal="`product-${product.id}`">
        <span>{{ $t('options') }}</span>
        <update-price :product="product" />
      </b-dropdown-item>

      <b-dropdown-item v-b-modal="`confirmDelete-${product.id}`">
        <span class="text-danger">Enviar a papelera</span>
      </b-dropdown-item>
    </b-dropdown>

    <b-row>
      <b-col
        cols="auto"
        class="pr-0"
      >
        <b-img
          width="100"
          rounded=""
          :src="`${product.product_catalog.logo}`"
          :alt="product.product_catalog.name || product.name"
        />
      </b-col>

      <b-col>
        <div class="d-flex align-items-center">
          <h3 class="mb-0 mr-1">
            {{ product.product_catalog.name || product.name }}
          </h3>

          <feather-icon
            v-if="product.is_special_delivery"
            :id="`tooltip-delivery-${product.id}`"
            icon="TruckIcon"
            size="18"
            color="#28c76f"
          />
          <b-tooltip
            v-if="product.is_special_delivery"
            :target="`tooltip-delivery-${product.id}`"
            triggers="hover"
            boundary-padding="10"
          >
            <span>{{ product.special_delivery_description }}</span>
          </b-tooltip>
        </div>

        <span class="text-muted text-small h5">
          {{ product.product_catalog.sku }} -
          <span class="font-weight-bolder text-dark">
            {{ product.product_catalog.brand || 'N/A' }}
          </span>
        </span>

        <div>
          <b-badge
            pill
            variant="primary"
            class="text-small"
          >
            {{ product.product_catalog.category.name }}
          </b-badge>
        </div>
        <div
          v-if="product.product_catalog.product_tags && product.product_catalog.product_tags.length"
          class="mt-25"
        >
          <b-badge
            v-for="tag in product.product_catalog.product_tags"
            :key="`tag-${tag.id}`"
            pill
            variant="light-info"
            class="text-small mb-25 mr-25"
          >
            {{ tag.name }}
          </b-badge>
        </div>

        <!-- Price block -->
        <div class="mt-1">
          <div class="d-flex align-items-baseline flex-wrap">
            <span class="price-main text-success">
              ${{ formatStockablePrice(product.price_list) }}
            </span>

            <span class="price-unit ml-50">
              / {{ $tc(unitsFilter(product.product_catalog.unit_type)) }}
              <span class="text-small text-muted ml-25">S/IVA</span>
            </span>
          </div>

          <!-- Previous price + delta + last updated -->
          <div
            v-if="product.previous_price_list != null || product.last_price_updated_at"
            class="price-meta d-flex align-items-center flex-wrap mt-25"
          >
            <template v-if="product.previous_price_list != null">
              <span class="text-muted text-small">
                Antes:
                <span class="font-weight-bolder">
                  ${{ formatStockablePrice(product.previous_price_list) }}
                </span>
              </span>

              <b-badge
                v-if="priceDelta !== null"
                pill
                :variant="priceDelta >= 0 ? 'light-success' : 'light-danger'"
                class="ml-50 price-pill"
              >
                <span class="font-weight-bolder">
                  {{ priceDelta >= 0 ? '+' : '' }}${{ formatStockablePrice(Math.abs(priceDelta)) }}
                </span>

                <span
                  v-if="priceDeltaPct !== null"
                  class="ml-25"
                >
                  ({{ priceDeltaPct >= 0 ? '+' : '' }}{{ priceDeltaPct.toFixed(1) }}%)
                </span>

                <feather-icon
                  :icon="priceDelta >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                  size="14"
                  class="ml-25"
                />
              </b-badge>
            </template>

            <span
              v-if="product.last_price_updated_at"
              class="text-small text-muted ml-50 d-flex align-items-center"
            >
              <feather-icon
                icon="ClockIcon"
                size="14"
                class="mr-25"
              />
              {{ timeAgo(product.last_price_updated_at) }}
            </span>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col class="border-top pt-1">
        <p class="mb-0">
          {{ product.product_catalog.description }}
        </p>
      </b-col>
    </b-row>

    <confirmation-modal
      :id="`confirmDelete-${product.id}`"
      :variant="'danger'"
      @confirm="deleteProduct(product.id)"
    >
      <template v-slot:content>
        Este producto se marcará como no manejado por la sucursal y se moverá a papelera.
      </template>
    </confirmation-modal>
    <div
      v-if="isDiscarded"
      class="mt-1 d-flex justify-content-end"
    >
      <b-button
        size="sm"
        variant="outline-primary"
        @click="restoreProduct(product.id)"
      >
        <feather-icon
          icon="RotateCcwIcon"
          size="14"
          class="mr-50"
        />
        Restaurar
      </b-button>
    </div>
  </b-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import UpdatePrice from '@/@core/components/UpdatePriceModal.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import { formatCurrencyNumber } from '@/utils/currency'

export default {
  components: {
    UpdatePrice,
    ConfirmationModal,
  },
  props: {
    product: {
      type: Object,
      default: () => ({
        name: '',
        category: '',
        categories_ids: [],
        img: '',
        price: 0,
        cost: 0,
        variant: '',
      }),
    },
    index: {
      type: Number,
      default() {
        return null
      },
    },
    isPos: {
      type: Boolean,
      default() {
        return false
      },
    },
    isInCart: {
      type: Boolean,
      default() {
        return false
      },
    },
    isDiscarded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('pos', ['posItems']),
    ...mapGetters('app', ['unitsFilter']),

    // Current - Previous
    priceDelta() {
      if (this.product?.previous_price_list == null || this.product?.price_list == null) return null
      const current = Number(this.product.price_list)
      const prev = Number(this.product.previous_price_list)
      if (Number.isNaN(current) || Number.isNaN(prev)) return null
      const d = current - prev
      return d === 0 ? null : d
    },

    // (Current - Previous) / Previous * 100
    priceDeltaPct() {
      if (this.priceDelta == null) return null
      const prev = Number(this.product.previous_price_list)
      if (!prev || Number.isNaN(prev)) return null
      return (this.priceDelta / prev) * 100
    },
  },
  methods: {
    ...mapMutations('pos', {
      addProduct: 'ADD_ITEM_TO_POS',
      removeProduct: 'REMOVE_ITEM_FROM_POS',
      changeQuantity: 'CHANGE_ITEM_QUANTITY',
    }),

    ...mapActions('products', ['deleteStockableProduct', 'restoreDiscardedStockableProduct']),

    restoreProduct(stockableId) {
      this.restoreDiscardedStockableProduct(stockableId).then(() => {
        this.$emit('product-updated')
      })
    },

    add() {
      if (this.$route.name === 'cart' || this.$route.name === 'registerSupplierProduct') {
        if (!this.posItems.find(item => item.id === this.product.product_catalog.id)) {
          this.addProduct(JSON.parse(JSON.stringify(this.product)))
        }
      }
    },

    remove(index) {
      if (this.$route.name === 'cart' || this.$route.name === 'registerSupplierProduct') {
        this.removeProduct({ index })
      }
    },

    deleteProduct(stockableId) {
      this.deleteStockableProduct(stockableId).then(() => {
        this.$emit('product-updated')
      })
    },

    formatStockablePrice(value) {
      return formatCurrencyNumber(value, { precision: 6 })
    },

    timeAgo(isoDate) {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return ''

      const diffMs = Date.now() - date.getTime()
      const diffMin = Math.floor(diffMs / 60000)
      const diffHrs = Math.floor(diffMin / 60)
      const diffDays = Math.floor(diffHrs / 24)

      if (diffMin < 1) return 'Actualizado hace unos segundos'
      if (diffMin < 60) return `Actualizado hace ${diffMin} min`
      if (diffHrs < 24) return `Actualizado hace ${diffHrs} h`
      if (diffDays < 7) return `Actualizado hace ${diffDays} día${diffDays === 1 ? '' : 's'}`

      return `Actualizado el ${date.toLocaleDateString()}`
    },
  },
}
</script>

<style lang="scss" scoped>
#dropdown-1 {
  position: absolute;
  top: 0;
  right: 0;
}

.text-small {
  font-size: 10px;
}

.price-main {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.1;
}

.price-unit {
  font-size: 0.95rem;
  color: #4b5563;
}

.price-meta {
  gap: 0.5rem;
  opacity: 0.92;
}

.price-pill {
  font-size: 0.72rem;
  padding: 0.22rem 0.5rem;
}

.product-card {
  border-radius: 12px;
}
</style>
